import { useCallback, useEffect, useState } from 'react'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import {
  EditFilenameTemplateItemInput,
  useEditFilenameModalMetadataQuery,
} from 'schema'
import { FilenameTemplate } from '@cims-online/shared'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { EditFilenameModalState, UseEditFilenameModal } from './types'
import { useProjectContext } from 'context/ProjectContext'

export function useEditFilenameModal<
  T extends EditFilenameModalState
>(): UseEditFilenameModal<T> {
  const [visible, toggle, state] = useCIMSModal<T>()
  const { projectId } = useProjectContext()
  const [templateItems, setTemplateItems] = useState<
    EditFilenameTemplateItemInput[]
  >([])
  const { data } = useEditFilenameModalMetadataQuery({
    ...DefaultQueryHookOptions,
    variables: { projectGlobalId: projectId },
  })
  const [valid, setValid] = useState(true)
  useEffect(() => {
    if (!state?.templateItems)
      return setTemplateItems(
        (state?.defaultTemplateItems ?? []).map((item) => {
          let value
          if (item.type === 'custom') value = item.text
          else value = item.templateItemId
          return { type: item.type, value }
        })
      )
    return setTemplateItems(
      (state?.templateItems ?? []).map(({ __typename, ...item }) => item)
    )
  }, [state?.defaultTemplateItems, state?.templateItems])
  useEffect(() => {
    setValid(
      templateItems.every(({ type, value }) => {
        if (type !== 'custom') return true
        return !FilenameTemplate.isIllegal(value)
      })
    )
  }, [templateItems])
  const isDisabled = useCallback<UseEditFilenameModal<T>['isDisabled']>(
    (item) => {
      if (!item) return true
      const { property } =
        typeof item === 'string' ? FilenameTemplate.fromIdOrFail(item) : item
      if (property === 'projectName' && !data?.project?.projectName) return true
      else if (
        property === 'clientProjectName' &&
        !data?.project?.clientProjectName
      )
        return true
      else if (
        property === 'clientProjectNumber' &&
        !data?.project?.clientProjectNumber
      )
        return true
      return false
    },
    [data?.project]
  )
  return {
    visible,
    toggle,
    state,
    templateItems,
    setTemplateItems,
    valid,
    isDisabled,
  }
}
